import { useEffect } from 'react';
import { useToast } from '../shared/ToastProvider';
import realtimeService from '../../services/realtimeService';

/**
 * An owned agent's direct message, shown to the person it was sent to.
 *
 * Where it comes from: Nunba's `chatbot_routes.agent_contact_request` delivers an
 * OWNED agent's message straight through (`creator_user_id == target_user_id`, so
 * no consent step) by calling `on_notification` with `type: 'agent_message'`. The
 * payload carries `agent_name`, `message`, `reason` and `request_id`.
 *
 * What used to happen: App.js received that event and wrote the payload to
 * `localStorage['agent_proactive_message']` with the comment "so Agent component
 * picks it up". Nothing in this repo ever read that key — or `active_agent_id` —
 * so an agent's message reached the browser and stopped. (Nunba's own copy of this
 * SPA does read `active_agent_id` from its chat UI; ours does not.)
 *
 * Why a component of its own: `App` RENDERS `ToastProvider`, so it cannot call
 * `useToast` itself. This has to sit inside the provider. It renders nothing.
 */
export default function AgentMessageToast() {
  // Destructure rather than holding the context object: ToastProvider passes a
  // fresh `value={{ showToast, dismissToast }}` literal on every render, and it
  // re-renders on every toast — depending on the object would resubscribe each
  // time. `showToast` is a useCallback([]) and is stable.
  const { showToast } = useToast() || {};

  useEffect(() => {
    if (!showToast) return undefined;
    return realtimeService.on('agent_message', (data) => {
      if (!data?.agent_id) return;
      // `reason` is the fallback the sender fills when it has no message body.
      const message = data.message || data.reason;
      if (!message) return;
      showToast('mention', {
        title: data.agent_name || 'Your agent',
        message,
      });
    });
  }, [showToast]);

  return null;
}
